









































import Vue from 'vue';
import _ from 'lodash';
import { sanityClient } from '@/config/sanity';
import VideoPublication from '@/models/videoPublication';
import Employee from '@/models/employee';
import Customer from '@/models/customer';
import LoadingIndicator from '@/components/LoadingIndicator.vue';
import ErrorMessage from '@/components/ErrorMessage.vue';
import CustomersPanel from '@/components/CustomersPanel.vue';
import TextWithHeadlinePanel from '@/components/TextWithHeadlinePanel.vue';
import TextWithPeoplePanel from '@/components/TextWithPeoplePanel.vue';
import EmployeesPanel from '@/components/EmployeesPanel.vue';
import SpeakerWithGradient from '@/components/SpeakerWithGradient.vue';
import {
  parseDateTimeFromSanityAutoGeneratedTimestamp,
  formatToLongDisplayFormat,
} from '@/utils/date';
import SanityVideoPublication from '@/models/sanitySchema/sanityVideoPublication';
import SanityEmployee from '@/models/sanitySchema/sanityEmployee';
import VideoGalleryPanel from './VideoGalleryPanel.vue';

interface MediaPage {
  introPanel: {
    headline: string;
    text: string;
  };
  videosPanel: {
    headline: string;
  };
  contactPanel: {
    headline: string;
    text: string;
    by: Array<Employee>;
  };
  customersPanel: {
    headline: string;
  };
  employeesPanel: {
    headline: string;
  };
}

interface Data {
  isLoading: boolean;
  error: string;
  mediaPage: MediaPage | null;
  videos: Array<VideoPublication>;
  employees: Array<Employee>;
  customers: Array<Customer>;
}

export default Vue.extend({
  name: 'Media',
  components: {
    LoadingIndicator,
    ErrorMessage,
    TextWithHeadlinePanel,
    TextWithPeoplePanel,
    VideoGalleryPanel,
    CustomersPanel,
    EmployeesPanel,
    SpeakerWithGradient,
  },
  data(): Data {
    return {
      isLoading: true,
      error: '',
      mediaPage: null,
      videos: [],
      employees: [],
      customers: [],
    };
  },
  created() {
    this.fetchMediaPage();
  },
  methods: {
    fetchMediaCustomers() {
      const query = '*[_type == "customer" && "media" in department]{ _id, name, logo }';
      return sanityClient.fetch(query).then(
        (sanityCustomers) => {
          this.customers = sanityCustomers;
        },
        (error) => {
          this.error = error;
        },
      );
    },
    fetchVideos() {
      const query = `*[_type == "videoPublication"] | order(order, _createdAt desc) {
        "createdAt": _createdAt,
        "updatedAt": _updatedAt,
        "id": _id,
        "key": _key,
        "slug": slug.current,
        title,
        author->{name, image, department, title},
        image,
        status,
        vimeoUrl,
        customer->{name},
        description,
        apparatInvolvement,
        credits,
        relatedVideos,
        order
      }[0...9]`;

      return sanityClient.fetch(query).then(
        (sanityVideoPublications) => {
          const videos = sanityVideoPublications.map((r: SanityVideoPublication) => {
            const createdAt = parseDateTimeFromSanityAutoGeneratedTimestamp(
              r.createdAt,
            );
            const createdAtFormatted = formatToLongDisplayFormat(createdAt);
            const updatedAt = parseDateTimeFromSanityAutoGeneratedTimestamp(
              r.updatedAt,
            );
            const updatedAtFormatted = formatToLongDisplayFormat(updatedAt);
            return {
              ...r,
              createdAt,
              createdAtFormatted,
              updatedAt,
              updatedAtFormatted,
            };
          });
          this.videos = videos;
        },
        (error) => {
          this.error = error;
        },
      );
    },
    fetchMediaEmployees() {
      const query = `*[_type == "employee"]{
        "createdAt": _createdAt,
        "updatedAt": _updatedAt,
        "id": _id,
        "key": _key,
        "slug": slug.current,
        name,
        title,
        department,
        image,
        order,
        person->{name,slug,image,title,department,order}
      }`;

      return sanityClient.fetch(query).then(
        (sanityEmployees) => {
          const mediaEmployees = sanityEmployees.filter((r: SanityEmployee) => {
            if (r.department) {
              return r.department === 'media';
            }
            if (r.person && r.person.department) {
              return r.person.department === 'media';
            }
            return false;
          });

          const normalizedEmployees = mediaEmployees.map((e: SanityEmployee) => {
            if (e.person) {
              return {
                name: e.person.name,
                title: e.person.title,
                department: e.person.department,
                image: e.person.image,
                order: e.person.order,
              };
            }
            return {
              name: e.name,
              title: e.title,
              department: e.department,
              image: e.image,
              order: e.order,
            };
          });

          const sortedEmployees = _.sortBy(normalizedEmployees, 'order', 'asc');
          this.employees = sortedEmployees;
        },
        (error) => {
          this.error = error;
        },
      );
    },
    fetchMediaPageFields() {
      const query = `*[_type == "mediaPage"]{
        "createdAt": _createdAt,
        "updatedAt": _updatedAt,
        "id": _id,
        "key": _key,
        "slug": slug.current,
        introductionPanelHeadline,
        introductionText,
        videosPanelHeadline,
        contactPanelHeadline,
        contactText,
        contactBy->{name,title,image,department},
        customersPanelHeadline,
        employeesPanelHeadline,
      }[0]`;

      return sanityClient.fetch(query).then(
        (page) => {
          this.mediaPage = {
            introPanel: {
              headline: page.introductionPanelHeadline,
              text: page.introductionText,
            },
            videosPanel: {
              headline: page.videosPanelHeadline,
            },
            contactPanel: {
              headline: page.contactPanelHeadline,
              text: page.contactText,
              by: page.contactBy ? [page.contactBy] : [],
            },
            customersPanel: {
              headline: page.customserPanelHeadline,
            },
            employeesPanel: {
              headline: page.employeesPanelHeadline,
            },
          };
        },
        (error) => {
          this.error = error;
        },
      );
    },
    fetchMediaPage() {
      this.isLoading = true;
      Promise.all([
        this.fetchMediaPageFields(),
        this.fetchMediaEmployees(),
        this.fetchVideos(),
        this.fetchMediaCustomers(),
      ]).then(() => {
        this.isLoading = false;
      }).catch(() => {
        this.isLoading = false;
      });
    },
  },
});
